import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { getProject, getProjectDashboard } from '../../../../Requests/ProjectRequests';
import CreateEditProjectSlideover from '../../../../Components/Projects/CreateEditProjectSlideover';
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../../utils/sidebar';
import TasksWidget from '../../../../Components/Tasks/TasksWidget';
import ProjectGanttWidget from '../../../../Components/Tasks/ProjectGanttWidget';
import ConversationsWidget from '../../../../Components/Conversations/ConversationsWidget';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import CircularProgress from '../../../../Components/Projects/CircularProgress';
import VerticalProgressSteps from '../../../../Components/Projects/VerticalProgressSteps';
import UpgradedLoader from '../../../../Components/UpgradedLoader';

function ProjectOverview() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { project_uuid } = useParams();
    const { isCollapsed } = useSidebar();
    const [project, setProject] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [editProjectSlideover, setEditProjectSlideover] = useState(null);
    // State for bento grid calendar
    const [calendarDate, setCalendarDate] = useState(new Date());

    // Set document title when component mounts or translation changes
    useEffect(() => {
        document.title = `${t('ProjectOverview.title')} - AIQDA`;
        return () => {
            // Reset to default title when leaving the component
            document.title = 'AIQDA';
        };
    }, [t]);

    useEffect(() => {
        if (project_uuid) {
            // Try consolidated dashboard API first (reduces 4-5 API calls to 1)
            getProjectDashboard(project_uuid).then((res) => {
                // If dashboard endpoint exists, use consolidated data
                setProject(res.data.project);
                setDashboardData(res.data);
                setDashboardLoading(false);
            }).catch((dashboardError) => {
                // Fallback to individual project call
                getProject(project_uuid).then((res) => {
                    setProject(res.data);
                    setDashboardData(null); // Widgets will make their own calls
                    setDashboardLoading(false); // Allow widgets to render now
                }).catch((error) => {
                    console.error('Error fetching project:', error);
                    navigate('/projects');
                });
            });
        }
    }, [project_uuid, navigate]);

    const handleEditProject = () => {
        setEditProjectSlideover(
            <CreateEditProjectSlideover
                project={project}
                onClose={() => setEditProjectSlideover(null)}
                onSuccess={(updatedProject) => {
                    setProject(updatedProject);
                    setEditProjectSlideover(null);
                }}
            />
        );
    };

    // Calendar navigation functions
    const goToPreviousMonth = () => {
        const newDate = new Date(calendarDate);
        newDate.setMonth(newDate.getMonth() - 1);
        setCalendarDate(newDate);
    };

    const goToNextMonth = () => {
        const newDate = new Date(calendarDate);
        newDate.setMonth(newDate.getMonth() + 1);
        setCalendarDate(newDate);
    };

    // Generate calendar for bento grid
    const generateBentoCalendar = () => {
        const today = new Date();
        const currentMonth = calendarDate.getMonth();
        const currentYear = calendarDate.getFullYear();
        const currentDate = today.getDate();
        const currentMonthToday = today.getMonth();
        const currentYearToday = today.getFullYear();
        
        const monthNames = [
            t('Calendar.MonthNames.January'),
            t('Calendar.MonthNames.February'), 
            t('Calendar.MonthNames.March'),
            t('Calendar.MonthNames.April'),
            t('Calendar.MonthNames.May'),
            t('Calendar.MonthNames.June'),
            t('Calendar.MonthNames.July'),
            t('Calendar.MonthNames.August'),
            t('Calendar.MonthNames.September'),
            t('Calendar.MonthNames.October'),
            t('Calendar.MonthNames.November'),
            t('Calendar.MonthNames.December')
        ];
        
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
        
        const days = [];
        
        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(null);
        }
        
        // Add all days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(day);
        }

        return {
            monthNames,
            currentMonth,
            currentYear,
            currentDate,
            currentMonthToday,
            currentYearToday,
            days
        };
    };

    if (!project) {
        return (
            <div className={`${getSidebarPadding(isCollapsed)} p-6`}>
                <div className="flex items-center justify-center py-12">
                    <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-black" />
                </div>
            </div>
        );
    }

    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 w-full flex justify-center`} data-testid="project-overview-container">
            <div className='flex flex-col w-full justify-center items-center px-4'>
                {/* Navigation Breadcrumb */}
                <div className="mb-6 w-full max-w-7xl">
                    <nav className="flex items-center space-x-2 text-sm text-gray-500">
                        <button
                            onClick={() => navigate('/projects')}
                            className="flex items-center hover:text-gray-700"
                            data-testid="back-to-projects-button"
                        >
                            <ChevronLeftIcon className="h-4 w-4 mr-1" />
                            {t('ProjectOverview.AllProjects')}
                        </button>
                        <span>›</span>
                        <span className="text-gray-900 font-medium">{project.name}</span>
                    </nav>
                </div>

                {/* Project Header Card */}
                <div className='bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl mx-8 mb-8'>
                    <div className="border-b border-gray-200 bg-white px-4 py-5 sm:px-6 rounded-2xl">
                        <div className="mt-2 md:flex md:items-center md:justify-between">
                            <div className="min-w-0 flex-1">
                                <h2 className="text-2xl/7 font-bold text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                                    {project.name}
                                </h2>
                                <p className="mt-1 text-sm text-gray-500 sm:truncate">
                                    {project.description || t('ProjectOverview.WelcomeMessage')}
                                </p>
                            </div>
                            <div className="mt-4 flex shrink-0 md:ml-4 md:mt-0">
                                <button
                                    type="button"
                                    onClick={handleEditProject}
                                    className="ml-3 inline-flex items-center rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100"
                                    data-testid="edit-project-button"
                                >
                                    {t('Projects.Edit')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Project Options Bento Grid */}
                <div className="w-full max-w-7xl mb-8">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-6 lg:grid-rows-2" data-testid="project-options-grid">
                        {/* Project Flow - Main Feature */}
                        <div className="relative lg:col-span-2 group" data-testid="project-option-flow">
                            <div className="absolute inset-0 rounded-lg bg-white max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]" />
                            <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-t-[calc(2rem+1px)] lg:rounded-tl-[calc(2rem+1px)] transition-all duration-200 group-hover:shadow-lg">
                                <div className="h-80 bg-white flex flex-col md:flex-row items-center md:justify-between justify-center p-8">
                                    <div className="flex-shrink-0 mb-6 md:mb-0 md:mr-8">
                                        <CircularProgress progress={Math.round(((project?.completed_steps ?? []).length / 4) * 100)} />
                                    </div>
                                    <div className="w-full md:flex-1 md:max-w-sm">
                                        <VerticalProgressSteps completedSteps={project?.completed_steps ?? []} />
                                    </div>
                                </div>
                                <div className="p-10 pt-4 flex flex-col flex-1">
                                    <p className="mt-2 text-lg font-medium tracking-tight text-gray-950">{t('ProjectOverview.ProjectFlow')}</p>
                                    <p className="mt-2 max-w-lg text-sm/6 text-gray-600">
                                        {t('ProjectOverview.ProjectFlowDescription')}
                                    </p>
                                    <div className="mt-auto flex justify-end">
                                        <button
                                            onClick={() => navigate(`/project/${project_uuid}/flow`)}
                                            className="rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100"
                                        >
                                            {t('ProjectOverview.Open')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5 max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]" />
                        </div>

                        {/* Calendar */}
                        <div className="relative lg:col-span-2">
                            <div className="absolute inset-0 rounded-lg bg-white" />
                            <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)]">
                                <div className="h-80 bg-white p-6">
                                    {(() => {
                                        const calendarData = generateBentoCalendar();
                                        const { 
                                            monthNames, 
                                            currentMonth, 
                                            currentYear, 
                                            currentDate,
                                            currentMonthToday,
                                            currentYearToday,
                                            days 
                                        } = calendarData;
                                        
                                        return (
                                            <div className="text-center">
                                                <div className="flex items-center justify-between mb-4">
                                                    <button
                                                        type="button"
                                                        onClick={goToPreviousMonth}
                                                        className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                                                    >
                                                        <span className="sr-only">{t('Calendar.PreviousMonth')}</span>
                                                        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                                    </button>
                                                    <h2 className="font-semibold text-gray-900">
                                                        {monthNames[currentMonth]} {currentYear}
                                                    </h2>
                                                    <button
                                                        type="button"
                                                        onClick={goToNextMonth}
                                                        className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                                                    >
                                                        <span className="sr-only">{t('Calendar.NextMonth')}</span>
                                                        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-7 mt-8 text-xs leading-6 text-center text-gray-500">
                                                    <div>{t('Calendar.DayNames.Sun')}</div>
                                                    <div>{t('Calendar.DayNames.Mon')}</div>
                                                    <div>{t('Calendar.DayNames.Tue')}</div>
                                                    <div>{t('Calendar.DayNames.Wed')}</div>
                                                    <div>{t('Calendar.DayNames.Thu')}</div>
                                                    <div>{t('Calendar.DayNames.Fri')}</div>
                                                    <div>{t('Calendar.DayNames.Sat')}</div>
                                                </div>
                                                <div className="grid grid-cols-7 mt-2 text-sm">
                                                    {days.map((day, dayIdx) => {
                                                        if (day === null) {
                                                            return <div key={dayIdx} />;
                                                        }
                                                        
                                                        // Check if this day is today (considering both current viewing month and actual today's month/year)
                                                        const isToday = day === currentDate && 
                                                                       currentMonth === currentMonthToday && 
                                                                       currentYear === currentYearToday;
                                                        
                                                        return (
                                                            <div key={day} className="py-1.5">
                                                                <button
                                                                    type="button"
                                                                    className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full ${
                                                                        isToday
                                                                            ? 'bg-iavisus-100 font-semibold text-white'
                                                                            : 'text-gray-900 hover:bg-gray-200'
                                                                    }`}
                                                                >
                                                                    <time dateTime={`${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`}>
                                                                        {day}
                                                                    </time>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="p-10 pt-4 flex flex-col flex-1">
                                    <p className="mt-2 text-lg font-medium tracking-tight text-gray-950">{t('ProjectOverview.Calendar')}</p>
                                    <p className="mt-2 max-w-lg text-sm/6 text-gray-600">
                                        {t('ProjectOverview.CalendarDescription')}
                                    </p>
                                    <div className="mt-auto flex justify-end">
                                        <button
                                            onClick={() => navigate(`/project/${project_uuid}/calendar`)}
                                            className="rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100"
                                        >
                                            {t('ProjectOverview.Open')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5" />
                        </div>

                        {/* Conversations Widget */}
                        <div className="relative lg:col-span-2">
                            <div className="absolute inset-0 rounded-lg bg-white lg:rounded-tr-[2rem]" />
                            <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] lg:rounded-tr-[calc(2rem+1px)]">
                                <div className="h-80 bg-white">
                                    {!dashboardLoading && (
                                        <ConversationsWidget 
                                            project={project} 
                                            preloadedData={dashboardData?.conversations}
                                        />
                                    )}
                                    {dashboardLoading && (
                                        <div className="flex items-center justify-center h-full">
                                            <UpgradedLoader className="animate-spin" w={6} h={6} bg="bg-iavisus-100" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-10 pt-4">
                                    <p className="mt-2 text-lg font-medium tracking-tight text-gray-950">{t('ProjectOverview.TeamConversations')}</p>
                                    <p className="mt-2 max-w-lg text-sm/6 text-gray-600">
                                        {t('ProjectOverview.TeamConversationsDescription')}
                                    </p>
                                    <div className="mt-auto flex justify-end">
                                        <button
                                            onClick={() => navigate(`/project/${project_uuid}/conversations`)}
                                            className="rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100"
                                        >
                                            {t('ProjectOverview.Open')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5 lg:rounded-tr-[2rem]" />
                        </div>

                        {/* Tasks Widget */}
                        <div className="relative lg:col-span-2">
                            <div className="absolute inset-0 rounded-lg bg-white lg:rounded-bl-[2rem]" />
                            <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] lg:rounded-bl-[calc(2rem+1px)]">
                                <div className="h-full">
                                    {!dashboardLoading && (
                                        <TasksWidget 
                                            project={project} 
                                            preloadedData={dashboardData?.tasks}
                                        />
                                    )}
                                    {dashboardLoading && (
                                        <div className="flex items-center justify-center h-full">
                                            <UpgradedLoader className="animate-spin" w={6} h={6} bg="bg-iavisus-100" />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5 lg:rounded-bl-[2rem]" />
                        </div>

                        {/* Project Timeline (Gantt Chart) - Expanded */}
                        <div className="relative lg:col-span-4">
                            <div className="absolute inset-0 rounded-lg bg-white max-lg:rounded-b-[2rem] lg:rounded-br-[2rem]" />
                            <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-b-[calc(2rem+1px)] lg:rounded-br-[calc(2rem+1px)]">
                                <div className="h-80 bg-white overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                    <div className="min-w-[800px] h-full">
                                        {!dashboardLoading && (
                                            <ProjectGanttWidget 
                                                project={project} 
                                                preloadedTasks={dashboardData?.tasks}
                                                preloadedCategories={dashboardData?.categories}
                                            />
                                        )}
                                        {dashboardLoading && (
                                            <div className="flex items-center justify-center h-full">
                                                <UpgradedLoader className="animate-spin" w={6} h={6} bg="bg-iavisus-100" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="p-10 pt-4">
                                    <p className="mt-2 text-lg font-medium tracking-tight text-gray-950">{t('ProjectOverview.ProjectTimeline')}</p>
                                    <p className="mt-2 max-w-lg text-sm/6 text-gray-600">
                                        {t('ProjectOverview.ProjectTimelineDescription')}
                                    </p>
                                    <div className="mt-auto flex justify-end">
                                        <button
                                            onClick={() => navigate(`/project/${project_uuid}/gantt`)}
                                            className="rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100"
                                        >
                                            {t('ProjectOverview.Open')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5 max-lg:rounded-b-[2rem] lg:rounded-br-[2rem]" />
                        </div>
                    </div>
                </div>

                {editProjectSlideover}
            </div>
        </div>
    );
}

export default ProjectOverview;